import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { BuscarPecasRow, FiltroCaracteristicas } from '@core/types';
import { codigoBateExato } from '../lib/bip';
import { suporteScanner, type SuporteScanner } from '../lib/scanner';
import LeitorCodigo from './LeitorCodigo';
import GaleriaCheia from './GaleriaCheia';
import OndeFica, { FaixaDoLugar, acaoPara, type AcaoNoOndeFica } from './OndeFica';
import { usePodeMudarEndereco } from '../lib/podeMudarEndereco';
import { useFotoSemFundo } from './MiniaturaDaPeca';
import {
  valoresEmUso,
  buscarPecas, carrinhosDasPecas, fornecedoresDePecas, fotosDasPecas,
  listarCaracteristicas, lugaresDasPecas, similaresDosResultados, urlDaFoto,
  type FornecedorDeResultado, type SimilarDeResultado,
} from '../lib/consultas';
import type { CarrinhoDaPecaRow } from '@core/types';
import { brl, qtd } from '../lib/formato';
import { lugaresParaMostrar, type LugaresDaPeca } from '../lib/ondeFica';
import { dominioDe, urlSegura } from '../lib/link';
import { descontoPct, emPromocao } from '../lib/promocao';
import { podeCadastroRapido, podeGerenciarCatalogo, useSessao } from '../lib/sessao';
import type { Papel } from '@core/types';
import { mensagemDeErro } from '../lib/supabase';
import Aviso from './Aviso';

// A tela que sozinha resolve a dor nº 1 (WEB_PLAN §2.5): achar a peça.
// Decisões que importam mais que o visual:
//   * campo único e grande — quem usa está em pé, segurando a peça na outra mão;
//   * busca a cada 250ms, mas Enter dispara na hora: o leitor de código de
//     barras USB digita tudo e manda Enter, então bipar já funciona no dia 1;
//   * a LOCALIZAÇÃO é o dado mais destacado do resultado. O preço vem depois:
//     primeiro é preciso achar a peça na prateleira. Desde 06/10/2026 ela é a
//     faixa "Onde fica" debaixo do cartão: um toque abre o mapa por cima da
//     tela, sem sair da busca nem da venda.

/** Para onde o "Cadastrar peça agora" leva: o completo é da gerência; o
 *  balcão cadastra pela tela própria (fila de conferência, 093). */
export const rotaDoCadastro = (papel: Papel | null) =>
  (podeGerenciarCatalogo(papel) ? '/cadastro' : '/cadastro/balcao');

/** O contêiner das linhas de resultado: o cartão é a LISTA, e cada peça é uma
 *  linha dele, separada pelo fio. A Busca guiada usa o mesmo. */
export const LISTA_DE_RESULTADOS = 'cartao divide-y divide-fio overflow-hidden';

/** O selo de cada situação (Grafite: o sinal mora no selo, com a palavra). A
 *  peça sem estoque é NEUTRA — não é atraso nem erro, é "encomendar"; o
 *  vermelho fica para o que deu errado. */
export const SELO_DA_SITUACAO = {
  ok: 'selo selo-pago',
  alerta: 'selo selo-hoje',
  erro: 'selo',
} as const;

/** O que a linha de baixo do preço diz. A peça aguardando liberação NÃO diz
 *  "sem estoque" em vermelho: ela nasce com saldo 0 de propósito (a contagem
 *  do colaborador só vira saldo na liberação), e o vermelho faria quem a
 *  cadastrou achar que o cadastro não pegou — e cadastrar de novo. */
export function situacaoDoCartao(peca: Pick<BuscarPecasRow, 'conferencia' | 'quantidade' | 'estoque_min'>):
  { texto: string; tom: 'erro' | 'alerta' | 'ok' } {
  if (peca.conferencia === 'pendente') return { texto: 'aguardando liberação', tom: 'alerta' };
  if (peca.quantidade <= 0) return { texto: 'sem estoque', tom: 'erro' };
  if (peca.estoque_min > 0 && peca.quantidade <= peca.estoque_min) {
    return { texto: `${qtd(peca.quantidade)} un`, tom: 'alerta' };
  }
  return { texto: `${qtd(peca.quantidade)} un`, tom: 'ok' };
}

export default function BuscaPecas({
  onEscolher, aoBiparExato, autoFoco = true, placeholder = 'Código, nome ou veículo…',
  mostrarFornecedores = false, mostrarSimilares = false, acaoNoOndeFica, buscarEmDestaque = false,
}: {
  onEscolher: (peca: BuscarPecasRow) => void;
  /**
   * Chamado SOZINHO quando a busca devolve UMA peça e o termo é exatamente um
   * dos códigos dela (bip do leitor USB) — aí o campo limpa e foca para o
   * próximo bip. Só a Venda passa isto: na Busca, escolher navega para a
   * ficha, e auto-navegar seria surpresa.
   */
  aoBiparExato?: (peca: BuscarPecasRow) => void;
  autoFoco?: boolean;
  placeholder?: string;
  /**
   * Mostra, sob cada resultado, quem costuma ter a peça — com link para o site
   * do fornecedor. Só a tela de Busca liga isto: na Venda seria ruído no meio
   * de um atendimento, e no orçamento também. Fornecedor é dado comercial, e
   * quem enxerga é a mesma régua de "Onde comprar" na ficha: gerência.
   */
  mostrarFornecedores?: boolean;
  /**
   * Mostra, sob cada resultado, as outras marcas do grupo de similares
   * (migração 027), com estoque e link para a ficha. Liga na Busca, para
   * QUALQUER papel — similar não é dado comercial, é o que evita dizer "não
   * tem" com a alternativa na prateleira.
   */
  mostrarSimilares?: boolean;
  /** O botão da folha "Onde fica": na Venda, "Pôr no carrinho"; na Busca, "Abrir a ficha". */
  acaoNoOndeFica?: AcaoNoOndeFica<BuscarPecasRow>;
  /**
   * O "Buscar" como o botão PRIMÁRIO (Grafite: um por tela). Só a tela de
   * Busca liga isto — lá, procurar é a ação da tela. Na Venda o primário é o
   * "Confirmar venda", e em Encomendas, Compras e Entrada a busca é o meio,
   * não o fim: o "Buscar" fica secundário (revisão do #359).
   */
  buscarEmDestaque?: boolean;
}) {
  const [texto, setTexto] = useState('');
  const [termo, setTermo] = useState('');
  const [filtros, setFiltros] = useState<FiltroCaracteristicas>({});
  // Câmera: o botão só aparece onde a leitura realmente funciona. No
  // iPhone/Safari o BarcodeDetector não existe, e um botão que abre a câmera
  // sem nunca ler é pior do que botão nenhum.
  const [suporte, setSuporte] = useState<SuporteScanner>('sem-api');
  const [lendo, setLendo] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);
  const { papel } = useSessao();

  // Só as características em LISTA viram filtro. Texto livre não vira: o filtro
  // é comparação exata, e campo digitado à mão nunca casa duas vezes igual —
  // ofereceria um filtro que quase sempre devolve vazio.
  const caracteristicas = useQuery({
    queryKey: ['caracteristicas', 'ativas'],
    queryFn: () => listarCaracteristicas(true),
    staleTime: 10 * 60_000,
  });
  // Os valores em uso abrem o filtro também para característica de texto e
  // número (036): "Motor" era invisível aqui porque só lista tem `opcoes`.
  const emUso = useQuery({
    queryKey: ['caracteristicas', 'valores-em-uso'],
    queryFn: valoresEmUso,
    staleTime: 5 * 60_000,
  });
  const filtraveis = (caracteristicas.data ?? [])
    .map((c) => ({
      ...c,
      opcoesFiltro: c.tipo === 'lista' && c.opcoes.length > 0
        ? c.opcoes
        : (emUso.data?.[c.id] ?? []),
    }))
    .filter((c) => c.opcoesFiltro.length > 0);

  useEffect(() => {
    const t = setTimeout(() => setTermo(texto), 250);
    return () => clearTimeout(t);
  }, [texto]);

  useEffect(() => { void suporteScanner().then(setSuporte); }, []);

  const temFiltro = Object.keys(filtros).length > 0;

  const { data, isFetching, error } = useQuery({
    // `filtros` entra na chave: sem isso, marcar "lado: direito" devolveria o
    // resultado guardado da busca sem filtro.
    queryKey: ['buscar', termo, filtros],
    queryFn: () => buscarPecas(termo, 25, filtros),
    // Com filtro marcado, buscar sem texto é legítimo ("tudo que é lado
    // direito") e a RPC trata esse caso.
    enabled: termo.trim().length >= 2 || temFiltro,
  });

  const alternarFiltro = (attributeId: string, valor: string) => {
    setFiltros((atual) => {
      const proximo = { ...atual };
      if (!valor) delete proximo[attributeId];
      else proximo[attributeId] = valor;
      return proximo;
    });
  };

  const buscarAgora = (e: FormEvent) => {
    e.preventDefault();
    setTermo(texto);
  };

  const limpar = () => {
    setTexto('');
    setTermo('');
    setFiltros({});
    entrada.current?.focus();
  };

  // Bip do leitor: código bateu EXATO com uma única peça → age sem clique.
  // `termo` nas dependências importa: bipar a MESMA peça de novo (2ª unidade)
  // devolve o mesmo `data` do cache, e é a mudança do termo que re-dispara.
  useEffect(() => {
    if (!aoBiparExato || !data || data.length !== 1) return;
    const unica = data[0]!;
    if (codigoBateExato(termo, unica)) {
      aoBiparExato(unica);
      limpar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, termo]);

  const resultados = data ?? [];

  // Uma consulta para TODOS os resultados, não uma por linha: a busca é
  // redigitada a cada 250 ms e uma rajada por tecla derrubaria a fluidez que a
  // tela inteira existe para ter. A chave é a lista de ids, então trocar o
  // termo sem trocar as peças reaproveita a resposta.
  const idsVisiveis = resultados.map((p) => p.product_id);
  const { data: fornecedoresPorPeca } = useQuery({
    queryKey: ['fornecedores-dos-resultados', idsVisiveis],
    queryFn: () => fornecedoresDePecas(idsVisiveis),
    enabled: mostrarFornecedores && idsVisiveis.length > 0,
    staleTime: 5 * 60_000,
  });

  const fornecedoresDe = (productId: string) =>
    (fornecedoresPorPeca ?? []).filter((f) => f.product_id === productId);

  // As miniaturas: mesma mecânica de lote (uma consulta para todos os
  // resultados), SEM recorte de papel — foto é catálogo, e a identificação de
  // relance serve ao vendedor tanto quanto à gerência. O binário só é baixado
  // quando a linha aparece na tela (loading="lazy" no <img>).
  const { data: fotosDosResultados } = useQuery({
    queryKey: ['fotos-dos-resultados', idsVisiveis],
    queryFn: () => fotosDasPecas(idsVisiveis),
    enabled: idsVisiveis.length > 0,
    staleTime: 60_000,
  });

  // A galeria INTEIRA de cada peça — as linhas chegam ordenadas, então a
  // primeira é a principal (a miniatura) e o modal desliza entre todas.
  const fotosDe = (productId: string) =>
    (fotosDosResultados ?? []).filter((f) => f.product_id === productId);

  // O "Luiz reservou 2 un": os carrinhos ativos dos colegas sobre os resultados
  // visíveis. Para TODO papel — é aviso de balcão, não dado comercial — e
  // atualizado a cada 15 s, porque reserva é coisa que nasce e morre em minutos.
  const { data: carrinhosPorPeca } = useQuery({
    queryKey: ['carrinhos-dos-resultados', idsVisiveis],
    queryFn: () => carrinhosDasPecas(idsVisiveis),
    enabled: idsVisiveis.length > 0,
    refetchInterval: 15_000,
  });
  const carrinhosDe = (productId: string) =>
    (carrinhosPorPeca ?? []).filter((c) => c.product_id === productId);

  // Os lugares extras (092) dos resultados, para o "também em" da faixa — o
  // principal já vem na busca. Mesma mecânica de lote das fotos.
  const { data: lugaresPorPeca } = useQuery({
    queryKey: ['lugares-das-pecas', idsVisiveis],
    queryFn: () => lugaresDasPecas(idsVisiveis),
    enabled: idsVisiveis.length > 0,
    staleTime: 60_000,
  });

  // Similares dos resultados visíveis, também numa consulta só, chaveada pelos
  // GRUPOS (não pelos ids): resultados diferentes do mesmo grupo reaproveitam.
  const gruposVisiveis = [...new Set(
    resultados.map((p) => p.grupo_similar).filter((g): g is string => Boolean(g)),
  )];
  const { data: similaresPorGrupo } = useQuery({
    queryKey: ['similares-dos-resultados', gruposVisiveis],
    queryFn: () => similaresDosResultados(gruposVisiveis),
    enabled: mostrarSimilares && gruposVisiveis.length > 0,
    staleTime: 60_000,
  });
  const similaresDe = (p: BuscarPecasRow) =>
    (similaresPorGrupo ?? []).filter(
      (s) => s.grupo_similar === p.grupo_similar && s.id !== p.product_id,
    );
  // Filtro marcado sem texto também é uma busca — senão a tela mostraria a
  // dica inicial no lugar de "nada encontrado" e pareceria que nada aconteceu.
  const buscou = termo.trim().length >= 2 || temFiltro;

  return (
    <div className="space-y-4">
      <form onSubmit={buscarAgora}>
        {/* O CAMPO GRANDE (Grafite): 56 px, a lupa à esquerda e a ação à
            direita. O "Buscar" só brilha (primário) na tela de Busca — ver
            `buscarEmDestaque`. */}
        <div className="relative">
          <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
               className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-mute"
               aria-hidden="true">
            <circle cx="8" cy="8" r="5.5" />
            <path d="M12.2 12.2 16 16" />
          </svg>
          <input
            ref={entrada}
            type="search"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={placeholder}
            aria-label="Buscar peça"
            autoFocus={autoFoco}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="search"
            className={`campo min-h-14 rounded-[0.875rem] py-3.5 pl-12 text-lg ${suporte === 'ok' ? 'pr-36' : 'pr-28'}`}
          />
          <div className="absolute inset-y-0 right-2 flex items-center gap-1">
            {suporte === 'ok' && (
              <button
                type="button"
                onClick={() => setLendo(true)}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-dim hover:text-paper"
                aria-label="Ler código com a câmera"
                title="Ler com a câmera"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                     strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
                  <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 12h10" />
                </svg>
              </button>
            )}
            {texto && (
              <button
                type="button"
                onClick={limpar}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-sm text-dim hover:text-paper"
                aria-label="Limpar busca"
              >
                ✕
              </button>
            )}
            <button type="submit"
                    className={`${buscarEmDestaque ? 'botao-primario' : 'botao-secundario'} min-h-11 px-3.5 py-2 text-sm`}>
              Buscar
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs text-dim">
          Pode digitar sujo (<span className="font-mono">KYB 334-115</span>), bipar o código de
          barras ou escrever o veículo (<span className="font-mono">celta 2012</span>).
        </p>

        {/* Filtro por característica (migração 017). Cada um que se marca
            ESTREITA: descrição + lado direito devolve só quem tem os dois.
            O bloco some quando a loja não criou nenhuma característica em
            lista — o balcão não ganha ruído por uma função que não usa. */}
        {filtraveis.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {filtraveis.map((c) => (
              <label key={c.id} className="flex items-center gap-1 text-xs">
                <span className="text-dim">{c.nome}</span>
                <select
                  className="campo py-1 text-sm"
                  value={filtros[c.id] ?? ''}
                  onChange={(e) => alternarFiltro(c.id, e.target.value)}
                  aria-label={`Filtrar por ${c.nome}`}
                >
                  <option value="">todos</option>
                  {c.opcoesFiltro.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
            ))}
            {temFiltro && (
              <button
                type="button"
                className="text-xs text-dim underline hover:text-paper"
                onClick={() => setFiltros({})}
              >
                limpar filtros
              </button>
            )}
          </div>
        )}
      </form>

      {error ? <Aviso tipo="erro">{mensagemDeErro(error)}</Aviso> : null}

      {isFetching && buscou && <p className="text-sm text-dim">Procurando…</p>}

      {buscou && !isFetching && resultados.length === 0 && (
        <div className="cartao p-6 text-center">
          <p className="font-semibold">Nada encontrado para "{termo}"</p>
          <p className="mt-2 text-sm text-mute">
            Pode ser peça ainda não cadastrada. Se ela existe na loja, cadastre agora — leva 60
            segundos e resolve para sempre.
          </p>
          {podeCadastroRapido(papel) && (
            /* O código bipado segue junto e cai preenchido no cadastro. A
               gerência vai ao cadastro completo; o balcão, à tela do balcão,
               onde a peça do vendedor nasce aguardando liberação (093). Esta
               é a ÚNICA porta de cadastro do vendedor. */
            <Link
              to={rotaDoCadastro(papel)}
              state={{ codigo: termo }}
              className="botao-secundario mt-4 inline-block px-4 py-2"
            >
              Cadastrar peça agora
            </Link>
          )}
        </div>
      )}

      {/* A leitura entra pelo MESMO caminho do bip USB: preenche o texto e
          dispara a busca na hora (sem esperar o debounce). Se o código bater
          exato com uma peça, o efeito de `aoBiparExato` age sozinho. */}
      {lendo && (
        <LeitorCodigo
          onFechar={() => setLendo(false)}
          onLeitura={(codigo) => {
            setLendo(false);
            setTexto(codigo);
            setTermo(codigo);
          }}
        />
      )}

      {/* A LISTA (Grafite): um cartão só, com a linha fina entre as peças —
          não uma pilha de cartões. É o que deixa o olho correr pela coluna
          do preço e pela do estoque. */}
      {resultados.length > 0 && (
        <ul className={LISTA_DE_RESULTADOS}>
          {resultados.map((p) => (
            <li key={p.product_id}>
              <ResultadoPeca peca={p} onEscolher={onEscolher}
                             fotos={fotosDe(p.product_id)}
                             fornecedores={fornecedoresDe(p.product_id)}
                             carrinhos={carrinhosDe(p.product_id)}
                             lugares={lugaresPorPeca?.get(p.product_id)}
                             acaoNoOndeFica={acaoNoOndeFica}
                             similares={mostrarSimilares ? similaresDe(p) : []} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ResultadoPeca({
  peca, onEscolher, fotos = [], fornecedores = [], carrinhos = [], similares = [], lugares, acaoNoOndeFica,
}: {
  peca: BuscarPecasRow;
  onEscolher: (p: BuscarPecasRow) => void;
  /** A galeria da peça, em ordem — a primeira é a miniatura; o toque nela abre
   *  o modal e desliza entre todas, sem sair da busca. */
  fotos?: Array<{ id: string; updated_at: string }>;
  fornecedores?: FornecedorDeResultado[];
  carrinhos?: CarrinhoDaPecaRow[];
  similares?: SimilarDeResultado[];
  /** Os lugares da peça (lugaresDasPecas). Sem eles, vale o principal que a busca trouxe. */
  lugares?: LugaresDaPeca;
  acaoNoOndeFica?: AcaoNoOndeFica<BuscarPecasRow>;
}) {
  const { session } = useSessao();
  const meuId = session?.user.id ?? null;
  const [galeriaAberta, setGaleriaAberta] = useState(false);
  // 'mudar' abre a folha já no "Pôr num lugar" (dono e gerente, peça sem lugar).
  const [ondeAberto, setOndeAberto] = useState<false | 'ver' | 'mudar'>(false);
  const podeMudarEndereco = usePodeMudarEndereco();
  // O lugar da consulta em lote é o mais novo (alguém pode ter mudado a peça
  // de lugar depois da busca); enquanto ele não chega, vale o da busca.
  const lugaresDaPeca = lugaresParaMostrar(lugares ? lugares.principal : peca.localizacao, lugares?.extras ?? []);
  const principal = fotos[0] ?? null;
  const fotoLimpa = useFotoSemFundo(peca.product_id, principal);
  const situacao = situacaoDoCartao(peca);
  // A AÇÃO DA LINHA, dita (Grafite: uma linha, um jeito de agir). É a mesma
  // que o toque no cartão já faz — a tela que passa o botão da folha "Onde
  // fica" passa o rótulo do próprio gesto ("Abrir a ficha" na Busca, "Pôr no
  // carrinho" na Venda). Sem ele, a linha não promete nada além do toque.
  const rotuloDaAcao = acaoNoOndeFica?.rotulo ?? null;

  return (
    <div className="transition-colors hover:bg-panel-alt/60">
      <div className="flex items-stretch">
      {/* A miniatura é um botão IRMÃO do cartão, não filho: tocar nela abre a
          foto em modal SEM sair da busca (pedido do dono, 12/08/2026) — e
          botão dentro de botão é HTML inválido, a mesma lição dos chips de
          fornecedor. O resto do cartão continua escolhendo a peça.
          Sem quadrado vazio quando não há foto: no meio do mutirão a maioria
          ainda não tem, e uma coluna de tracejados só faria ruído. lazy: o
          binário só desce quando a linha aparece — segura o 4G. */}
      {principal && (
        <button
          type="button"
          onClick={() => setGaleriaAberta(true)}
          className="shrink-0 self-start pl-4 pt-3.5"
          aria-label="Ver a foto da peça"
        >
          {/* Sem o fundo branco (01/10/2026): a peça recortada, inteira
              (contain), no lugar do quadrado branco cortado (cover). */}
          {fotoLimpa
            ? <img src={fotoLimpa} alt="" loading="lazy"
                   className="h-14 w-14 rounded-[0.625rem] bg-panel-alt object-contain" />
            : <span className="block h-14 w-14 animate-pulse rounded-[0.625rem] bg-edge/40" aria-hidden="true" />}
        </button>
      )}
      <button
        type="button"
        onClick={() => onEscolher(peca)}
        className="min-w-0 flex-1 px-4 py-3.5 text-left"
      >
      <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
          <p className="text-[0.9375rem] font-medium leading-snug text-paper">
            {peca.descricao}
            {/* O código DO FABRICANTE como etiqueta, colado no nome: é a
                nomenclatura estampada na peça — o que o cliente lê no balcão e
                o que se confere contra a caixa. Pedido do dono (11/08/2026),
                nos moldes do catálogo do fornecedor. */}
            {peca.codigo_fabrica && (
              <span className="numero ml-2 inline-block rounded-md bg-edge px-1.5 py-0.5 align-middle text-xs text-paper">
                {peca.codigo_fabrica}
              </span>
            )}
          </p>
          {/* O código da loja em mono, a marca e o lado na mesma linha miúda. */}
          <p className="mt-1 text-xs text-dim">
            <span className="numero">{peca.sku}</span>
            {peca.marca ? ` · ${peca.marca}` : ''}
            {peca.posicao ? ` · ${peca.posicao}` : ''}
          </p>
          </div>

        {/* No celular: preço em cima, selo embaixo. Do sm em diante, uma fileira
            só — selo, preço e a ação — como as colunas do desenho. */}
        <div className="flex shrink-0 flex-col items-end gap-1.5 text-right sm:flex-row sm:items-center sm:gap-3">
          {/* Em promoção: tabela rasurada, preço vigente e o selo do desconto.
              É o "de X por Y" que o vendedor usa para fechar — e o preço que a
              Venda pré-preenche é o mesmo (precoVigente), sem chance de cobrar
              o cheio por esquecimento. */}
          {emPromocao(peca) ? (
            <span className="block">
              <span className="numero block text-xs text-dim line-through">{brl(peca.preco_balcao)}</span>
              <span className="flex items-center justify-end gap-1.5">
                {(descontoPct(peca) ?? 0) > 0 && (
                  <span className="selo numero">−{descontoPct(peca)}%</span>
                )}
                <span className="numero text-base text-paper sm:text-lg">{brl(peca.preco_promocional)}</span>
              </span>
            </span>
          ) : (
            <span className="numero block text-base text-paper sm:text-lg">{brl(peca.preco_balcao)}</span>
          )}
          {/* O estoque em SELO, com a palavra. A pendente diz "aguardando
              liberação" no tom de atenção: é estado da peça, não contagem. */}
          <span className={`${SELO_DA_SITUACAO[situacao.tom]} sm:order-first`}>{situacao.texto}</span>
          {rotuloDaAcao && (
            <span aria-hidden="true"
                  className="botao-secundario pointer-events-none hidden px-3 py-1.5 text-xs sm:inline-flex">
              {rotuloDaAcao}
            </span>
          )}
        </div>
      </div>

      {/* Só o OEM aqui: o código do fabricante subiu para a etiqueta ao lado
          do nome, e repetido viraria ruído. O LUGAR saiu daqui (06/10/2026):
          virou a faixa "Onde fica" logo abaixo do cartão, que abre o mapa. */}
      {peca.codigo_oem && (
        <p className="numero mt-2 text-xs text-mute">{peca.codigo_oem}</p>
      )}

      {/* Observações do balcão em letra miúda: o macete que decide a venda
          ("serve também no Uno até 2004") aparece SEM abrir a ficha. */}
      {peca.observacoes && (
        <p className="mt-2 line-clamp-2 text-xs italic text-dim">{peca.observacoes}</p>
      )}

      {peca.aplicacoes && (
        <p className="mt-2 line-clamp-2 text-xs text-mute">{peca.aplicacoes}</p>
      )}

      {/* Reserva de balcão (migração 026): quem está com a peça no carrinho
          AGORA. Aviso operacional para todo papel — o vendedor decide se
          disputa, espera os 30 min ou resolve na voz, que numa loja de cinco
          pessoas é o protocolo mais rápido que existe. O sinal é o PONTO de
          atenção; o texto fica legível, no tom de apoio. */}
      {carrinhos.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {carrinhos.map((c) => (
            <p key={`${c.vendedor_id}-${c.product_id}`} className="flex items-center gap-1.5 text-xs text-mute">
              <span className="ponto text-alerta" aria-hidden="true" />
              <span>
                {c.vendedor_id === meuId ? 'No seu carrinho' : `${c.vendedor} reservou`}
                {' '}<span className="numero">{qtd(c.quantidade)}</span> un
                {c.cliente_nome ? ` para ${c.cliente_nome}` : ''}
              </span>
            </p>
          ))}
        </div>
      )}
      </button>
      </div>

      {/* Localização em destaque: é o que faz a busca terminar em 30 segundos. */}
      <FaixaDoLugar lugares={lugaresDaPeca} area={lugares?.area ?? null} localBruto={lugares ? lugares.principal : peca.localizacao}
                    aoAbrir={() => setOndeAberto('ver')}
                    aoPorNumLugar={podeMudarEndereco ? () => setOndeAberto('mudar') : undefined} />

      {galeriaAberta && (
        <GaleriaCheia
          fotos={fotos}
          urlDe={(f) => urlDaFoto(f.id, f.updated_at)}
          aoFechar={() => setGaleriaAberta(false)}
        />
      )}

      {ondeAberto && (
        <OndeFica
          aoFechar={() => setOndeAberto(false)}
          comecarMudando={ondeAberto === 'mudar'}
          peca={{
            product_id: peca.product_id, descricao: peca.descricao, sku: peca.sku, marca: peca.marca,
            quantidade: peca.quantidade, lugares: lugaresDaPeca, area: lugares?.area ?? null,
          }}
          acao={acaoPara(acaoNoOndeFica, peca)}
        />
      )}

      {similares.length > 0 && <ChipsSimilares similares={similares} />}
      {fornecedores.length > 0 && <ChipsFornecedor fornecedores={fornecedores} />}
    </div>
  );
}

/** O chip de link da linha (similar, fornecedor): pílula neutra, e 44 px de
 *  alvo no celular — no computador ele encolhe para não engordar a lista. */
const CHIP = 'inline-flex min-h-11 items-center gap-1.5 rounded-full border border-edge-forte bg-panel-alt px-2.5 '
  + 'text-xs text-mute transition-colors hover:bg-edge hover:text-paper sm:min-h-8';

/**
 * As outras marcas do grupo de similares, com o estoque de cada uma na cara —
 * verde tem, vermelho não tem. É o pulo do gato do balcão: a NGK acabou, mas o
 * chip mostra "Bosch · 4" antes de alguém dizer "não tem".
 *
 * FORA do botão do cartão, como os fornecedores: Link dentro de button é HTML
 * inválido e o navegador engole um dos cliques. Clicar no chip abre a FICHA da
 * similar — é lá que estão local, preço e o botão Vender dela.
 */
function ChipsSimilares({ similares }: { similares: SimilarDeResultado[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-t border-fio px-4 py-2">
      <span className="text-[0.6875rem] uppercase tracking-wider text-dim">similares</span>
      {similares.map((s) => (
        <Link
          key={s.id}
          to={`/peca/${s.id}`}
          title={`${s.marca ?? s.sku} — ${brl(s.preco_promocional ?? s.preco_balcao)}`}
          className={CHIP}
        >
          {s.marca ?? s.sku}
          {/* O número leva o sinal (Grafite): verde tem, vermelho não tem. */}
          <span className={['numero', s.quantidade > 0 ? 'text-ok' : 'text-erro'].join(' ')}>
            {qtd(s.quantidade)}
          </span>
        </Link>
      ))}
    </div>
  );
}

/**
 * Quem costuma ter esta peça, em chips clicáveis — mesmo desenho das marcas na
 * home do site (borda fina, sem preenchimento), em corpo menor porque aqui eles
 * acompanham um resultado, não são o assunto.
 *
 * FORA do botão do cartão de propósito: `<a>` dentro de `<button>` é HTML
 * inválido e o navegador engole um dos dois cliques. Por isso o cartão virou
 * uma div com o botão dentro.
 *
 * Sem site cadastrado o chip continua aparecendo, só que sem link: saber QUEM
 * tem a peça já vale, mesmo sem para onde clicar.
 */
function ChipsFornecedor({ fornecedores }: { fornecedores: FornecedorDeResultado[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-t border-fio px-4 py-2">
      <span className="text-[0.6875rem] uppercase tracking-wider text-dim">tem em</span>
      {fornecedores.map((f) => {
        const site = urlSegura(f.site);
        const titulo = f.codigo_fornecedor
          ? `${f.nome} — código ${f.codigo_fornecedor}`
          : f.nome;
        return site ? (
          <a
            key={f.supplier_id}
            href={site}
            target="_blank"
            rel="noreferrer"
            title={`Abrir ${dominioDe(site)}${f.codigo_fornecedor ? ` · código ${f.codigo_fornecedor}` : ''}`}
            className={CHIP}
          >
            {f.nome}
          </a>
        ) : (
          <span key={f.supplier_id} title={titulo}
                className="inline-flex min-h-8 items-center rounded-full border border-dashed border-edge px-2.5 text-xs text-dim">
            {f.nome}
          </span>
        );
      })}
    </div>
  );
}
